import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useConditionalEffect } from 'src/commons';
import { fetchActivityInfo, fetchActivityRevision } from 'src/data/api/fetchFromAPI';
import { HammerstoneAppLayout } from '../helpers';
import PAGES from 'src/nav/Pages';
import { Header, Select, SpaceBetween } from '@amzn/awsui-components-console';
import ActivityContainerLayout from './ActivityContainerLayout';
import { DisplayMode } from '../helpers/content/contentInterfaces';
import store, { actions, useActivity, useGroupName } from 'src/data/redux';
import { activityRevisionIdentifier } from 'src/interfaces/activityInterfaces';
import { urlSearchString } from 'src/nav/navHelper';

function currentRevisionFlashbar() {
	store.dispatch(
		actions.page.addToFlashbar({
			id: 'current_activity_revision',
			persistAcrossApp: true,
			message: {
				type: 'info',
				header: 'Displaying current activity configuration',
				content: `The activity's latest revision points to its current configuration; to see the current revision, go to the activity page`,
				dismissible: true,
			},
		})
	);
}

function invalidRevisionFlashbar(requestedRevision: number, activityRevision: number) {
	store.dispatch(
		actions.page.addToFlashbar({
			id: `invalid_activity_revision_${requestedRevision}`,
			persistAcrossApp: true,
			message: {
				type: 'error',
				header: `Invalid revision ${requestedRevision} provided`,
				content: `This activity has ${activityRevision} revisions; this UI can only show previous revisions`,
			},
		})
	);
}

export default function ActivityRevision() {
	// Pull the params and pass them in as props
	const { activityId, revision, diffRevision } = useParams();
	return (
		<_ActivityRevision
			activityId={parseInt(activityId)}
			fromRevision={parseInt(revision)}
			toRevision={parseInt(diffRevision)}
		/>
	);
}

export function _ActivityRevision(props: { activityId: number; fromRevision: number; toRevision?: number }) {
	const { activityId, fromRevision, toRevision } = props;
	const groupName = useGroupName();
	const search = useMemo(() => urlSearchString({ groupName }), [groupName]);
	const navigate = useNavigate();

	const { data: activity, fetching: fetchingActivity } = useActivity(activityId.toString());

	const isFromRevisionCurrent = fromRevision === activity?.revision;
	const isToRevisionCurrent = toRevision === activity?.revision;

	const fromRevisionId = activityRevisionIdentifier({ activityId, revision: fromRevision });
	const toRevisionId = activityRevisionIdentifier({ activityId, revision: toRevision });

	const fromRevisionSlice = useActivity(fromRevisionId);
	const toRevisionSlice = useActivity(toRevisionId);

	const isDiff = toRevision > 0 && toRevision !== fromRevision;

	useConditionalEffect(() => {
		// Use this call to verify the activity, group name, and revision number
		fetchActivityInfo({ groupName, activityId });
	}, [activityId, groupName]);

	useConditionalEffect(() => {
		// Once the activity is loaded, validate that the "from" revision is valid and then retrieve it
		if (0 < fromRevision && fromRevision < activity.revision) {
			// Valid revision, fetch from API and persist to redux store
			fetchActivityRevision({ groupName, activityId, revision: fromRevision });
		} else if (isFromRevisionCurrent) {
			// Max revision, use current activity
			currentRevisionFlashbar();
		} else {
			invalidRevisionFlashbar(fromRevision, activity.revision);
		}
	}, [groupName, activity?.revision, activityId, fromRevision]);

	useConditionalEffect(() => {
		// Once the activity is loaded, validate that the "to" revision (if provided) and then retrieve it
		if (0 < toRevision && toRevision < activity.revision) {
			if (toRevision < fromRevision) {
				// If the "to" revision is less than the "from" revision, flip the order of arguments
				navigate({
					pathname: PAGES.DIFF_ACTIVITY_REVISION.path
						.replace(':activityId', activityId.toString())
						.replace(':revision', toRevision.toString())
						.replace(':diffRevision', fromRevision.toString()),
					search,
				});
			} else {
				// Otherwise, fetch from the API and persist to redux store
				fetchActivityRevision({ groupName, activityId, revision: toRevision });
			}
		} else if (isToRevisionCurrent) {
			// Max revision, use current activity
			currentRevisionFlashbar();
		} else {
			invalidRevisionFlashbar(toRevision, activity.revision);
		}
	}, [groupName, activity?.revision, activityId, toRevision, fromRevision]);

	const options = useMemo(
		() =>
			Array.from({ length: activity?.revision || 0 }, (_, i) => {
				const revision = i + 1;
				return {
					value: revision.toString(),
					label: revision === activity?.revision ? `${revision} (current)` : revision.toString(),
				};
			}),
		[activity?.revision]
	);

	return (
		<HammerstoneAppLayout
			{...PAGES[isDiff ? 'DIFF_ACTIVITY_REVISION' : 'VIEW_ACTIVITY_REVISION'].pageLayoutProps({
				activityId,
				revision: fromRevision,
				diffRevision: toRevision,
			})}
		>
			<SpaceBetween size="xl">
				<Header
					variant="h1"
					actions={
						<SpaceBetween size="xs" direction="horizontal">
							<Select
								inlineLabelText="From revision"
								selectedOption={props.fromRevision && { value: `${props.fromRevision}` }}
								options={options}
								statusType={activity ? 'finished' : fetchingActivity ? 'loading' : 'pending'}
								onChange={({ detail }) => {
									if (props.toRevision) {
										navigate({
											pathname: PAGES.DIFF_ACTIVITY_REVISION.path
												.replace(':activityId', activityId.toString())
												.replace(':revision', detail.selectedOption.value)
												.replace(':diffRevision', props.toRevision.toString()),
											search,
										});
									} else {
										navigate({
											pathname: PAGES.VIEW_ACTIVITY_REVISION.path
												.replace(':activityId', activityId.toString())
												.replace(':revision', detail.selectedOption.value),
											search,
										});
									}
								}}
								loadingText="Loading activity..."
							/>{' '}
							diff with →{' '}
							<Select
								inlineLabelText="To revision"
								selectedOption={props.toRevision && { value: `${props.toRevision}` }}
								options={options}
								statusType={activity ? 'finished' : fetchingActivity ? 'loading' : 'pending'}
								onChange={({ detail }) => {
									navigate({
										pathname: PAGES.DIFF_ACTIVITY_REVISION.path
											.replace(':activityId', activityId.toString())
											.replace(':revision', props.fromRevision.toString())
											.replace(':diffRevision', detail.selectedOption.value),
										search,
									});
								}}
								loadingText="Loading activity..."
							/>
						</SpaceBetween>
					}
				>
					View activity {activityId}, revision {fromRevision} {isDiff && `diff → ${toRevision}`}
				</Header>
				<ActivityContainerLayout
					mode={
						fetchingActivity || fromRevisionSlice.fetching || toRevisionSlice.fetching
							? DisplayMode.Loading
							: isDiff
							? DisplayMode.Diff
							: DisplayMode.View
					}
					// When the max revision is selected, use just the activityId as resourceId to point to the current configuration
					activityId={isFromRevisionCurrent ? activityId.toString() : fromRevisionId}
					diffActivityId={isToRevisionCurrent ? activityId.toString() : toRevisionId}
					activityType={activity?.activityType}
				/>
			</SpaceBetween>
		</HammerstoneAppLayout>
	);
}
